/**
 * 🔴🔴 2026-10-16：**引用稳定化** —— 轮询型重拉的「无事发生即零重渲染」。
 *
 * ## 事故（老板反复反馈两次）
 *
 * > 「页面还是会自动重新载入」「还是自己在刷新页面」
 *
 * 应用里有**多个轮询**在无条件整项目重拉：
 *   · `scenes/page.tsx`  —— 每 8–20 秒 `refreshProject`（**永不停**）
 *   · `pipeline/page.tsx` —— 每 5 秒
 *   · `use-extract-status` / `episode-card` / `agent-console` —— 4–6 秒
 *
 * `fetchProject` 每次 `set({ project: data })` 都是**全新对象** ⇒ 即使数据一字未变，
 * 所有 `useEffect(..., [project])` 也会重跑、所有订阅 `project` 的组件都重渲染。
 * 用户感受就是「**页面自己在刷新**」，正打字时会被打断。
 *
 * ## 为什么改这里，而不是逐个去改轮询
 *
 * 全项目 51 处 `fetchProject/refreshProject`，逐个改既漏又脆；
 * 而**引用稳定化是普适的**，一次覆盖全部调用点：
 *   · 内容相同 ⇒ 复用原引用 ⇒ React 视为无变化 ⇒ **零重渲染**
 *   · 内容变了 ⇒ 只有**真正变的那部分**拿新引用 ⇒ 最小重渲染
 *     （`shots` 逐镜对齐：71 镜里改一镜，只有那一张卡重渲染）
 *
 * ## 为什么这不说谎
 *
 * 只动**引用**、不动**数据**：相等才复用，不等一律用服务端新值。
 * 语义与改造前完全一致 —— 只是「没变的东西不再假装自己变了」。
 *
 * ⚠️ 本模块**刻意零依赖**（纯函数）：抽出来单独放，既好测（可直接行为对拍），
 *   也避免被 store 的重依赖链拖进 `--experimental-strip-types` 的 strip-only 限制。
 */

/** 引用相等或 JSON 值相等（不可序列化时按「不等」处理，保守取服务端新值） */
export function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * 把「内容其实没变」的部分换成原引用；**整体没变时返回 `current` 本身**。
 *
 * @param current 当前 store 里的对象（可为 null）
 * @param next    服务端刚返回的新对象
 */
export function stabilizeProject<T extends Record<string, unknown>>(
  current: T | null | undefined,
  next: T,
): T {
  if (!current || typeof next !== "object" || next === null) return next;
  const out: Record<string, unknown> = { ...next };
  /**
   * 🔴 关键：**一个字段都没变时必须返回 `current` 本身**（不是 `{...next}`）。
   *
   * 只复用内部字段是不够的 —— 顶层对象若是新引用，`useEffect(..., [project])`
   * 照样重跑、订阅 `project` 的组件照样重渲染，「零重渲染」就落空。
   * 轮询场景下「无事发生」是绝大多数轮次，这一步才是真正止血的地方。
   */
  let changed = false;
  for (const key of Object.keys(next)) {
    const a = (current as Record<string, unknown>)[key];
    const b = (next as Record<string, unknown>)[key];
    if (a === b) continue;
    // `shots` 是 71 镜的大数组：整体换引用 = 整个镜头列表重渲染 ⇒ 逐镜对齐
    if (key === "shots" && Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
      const prevShots = a as unknown[];
      let anyShotChanged = false;
      const aligned = (b as unknown[]).map((row, i) => {
        if (sameJson(prevShots[i], row)) return prevShots[i]; // 未变 ⇒ 复用原镜对象
        anyShotChanged = true;
        return row;
      });
      /**
       * 🔴 全部未变时必须**连数组引用一起复用**（`prevShots`），不能赋值新建的 `aligned`。
       *   否则虽然每个镜对象都复用了，数组本身是新引用 ⇒ 列表容器仍会重渲染，
       *   「最小重渲染」打了个折扣。（此条由行为断言 ⑤ 抓出 —— 源码看着对，行为不达标。）
       */
      out[key] = anyShotChanged ? aligned : prevShots;
      if (anyShotChanged) changed = true;
      continue;
    }
    if (sameJson(a, b)) {
      out[key] = a; // 复用原引用
    } else {
      changed = true;
    }
  }
  // 服务端不再返回的键（字段被删）也算变化 —— 否则会拿旧值掩盖「已删除」这一事实
  if (!changed && Object.keys(next).length !== Object.keys(current).length) changed = true;
  return (changed ? out : (current as unknown as Record<string, unknown>)) as T;
}
